<script lang="ts" setup>
  import { useOrigDestData } from '@/composables/orig-dest/useOrigDestData'
  import { useOrigDestSearch } from '@/composables/orig-dest/useOrigDestSearch'
  import { announce } from '@/utils/a11yAlert'
  import { CATEGORIES, ORIGIN, DESTIN } from '@/utils/constants/origDest'
  import type { ICategories, TDestin, IOrigin, TSubCategories, TInputVariant } from '@/types/OrigDest'
  import { cva } from 'class-variance-authority'
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'

  const { t, locale } = useI18n()
  const { isDesktop } = useBTBreakpoints()

  const props = withDefaults(
    defineProps<{
      variant?: TInputVariant
      allowSwap?: boolean
      watchQueryParams?: boolean
      showAnyDestinOption?: boolean
      openOverTrigger?: boolean
    }>(),
    {
      variant: 'default',
      allowSwap: true,
      watchQueryParams: false,
      showAnyDestinOption: true,
      openOverTrigger: false
    }
  )

  const origDestData = useOrigDestData()

  onMounted(() => {
    origDestData.fetchOrigDestData(locale.value)
  })

  if (props.watchQueryParams) {
    const fsfStore = useFsfStore()

    watch(
      () => origDestData.selectedOrigin.value,
      (origin) => {
        fsfStore.origin = origin?.code
        fsfStore.originType = origin?.type
      }
    )

    watch(
      () => origDestData.selectedDestin.value,
      (destin) => {
        fsfStore.destin = destin?.code
        fsfStore.destinType = destin?.type
      }
    )
  }

  const emit = defineEmits<{
    destinationSelected: []
  }>()

  const route = useRoute()
  const onDestinationSelected = () => {
    if (route.meta.pageType !== 'index') return

    emit('destinationSelected')
  }

  onMounted(() => {
    origDestData.setDataFomUrl()
  })

  const isOriginOpen = ref(false)
  const openOrigin = () => {
    if (!isDestinDisabled.value) return

    isOriginOpen.value = true
  }

  const isDestinDisabled = computed(() => !origDestData.selectedOrigin.value)

  const { searchTerm, openedOrigDest, filteredOrigDestData } = useOrigDestSearch(origDestData)

  const getTitle = (selected: IOrigin | TDestin | null, type: typeof ORIGIN | typeof DESTIN) => {
    if (!selected) {
      return t(`origDest.${type}.select`)
    }

    let title = selected?.city ?? null

    if (selected?.code) {
      title = title ? t('origDest.selected.aptCode', { apt: title, code: selected.code }) : selected.code
    }

    return title ?? t(`origDest.${type}.select`)
  }
  const selectedTitle = computed(() => {
    return {
      origin: getTitle(origDestData.selectedOrigin.value, ORIGIN),
      destin: getTitle(origDestData.selectedDestin.value, DESTIN)
    }
  })

  const originTriggerAriaLabel = computed(() => t('aria.label.originWithCity', { city: selectedTitle.value.origin }))
  const destinationTriggerAriaLabel = computed(() =>
    t('aria.label.destinWithCity', { city: selectedTitle.value.destin })
  )

  const originCategories: ICategories[] = [
    { value: t('origDest.categories.countries'), key: CATEGORIES.COUNTRIES },
    { value: t('origDest.categories.all'), key: CATEGORIES.ALL }
  ]
  const originSubCategories = computed<TSubCategories[] | []>(() => {
    if (!origDestData.selectedCategory.value.origin) return []

    if (origDestData.selectedCategory.value.origin === CATEGORIES.COUNTRIES) {
      return origDestData.originCountries.value
    }

    return []
  })

  const destinationCategories = computed(() => {
    const { countries, labels, direct } = origDestData.destinationLabelsAndCountries.value
    const categories: ICategories[] = []

    if (direct?.length) {
      categories.push({ value: t('origDest.categories.direct'), key: CATEGORIES.DIRECT })
    }

    categories.push({ value: t('origDest.categories.all'), key: CATEGORIES.ALL })

    if (countries?.length) {
      categories.push({ value: t('origDest.categories.countries'), key: CATEGORIES.COUNTRIES })
    }

    if (labels?.length) {
      categories.push({ value: t('origDest.categories.interests'), key: CATEGORIES.INTERESTS })
    }

    return categories
  })

  const destinationSubCategories = computed(() => {
    const { countries, labels } = origDestData.destinationLabelsAndCountries.value

    if (origDestData.selectedCategory.value.destin === CATEGORIES.COUNTRIES) {
      return countries
    }

    if (origDestData.selectedCategory.value.destin === CATEGORIES.INTERESTS) {
      return labels
    }

    return []
  })

  onMounted(() => {
    watch(
      () => isDesktop.value,
      (val) => {
        if (!val) {
          origDestData.resetCategories()
        }
      },
      { flush: 'post' }
    )
  })

  const swapBase = computed(() =>
    cva(
      'flex items-center bg-white !text-bt-blue-ui !size-10 flex-shrink-0 flex-grow-0 border border-border rounded-sm p-1 justify-center hover:border-bt-blue-dark',
      {
        variants: {
          style: {
            default: 'relative top-3',
            block: 'absolute left-1/2 top-1/2 z-1  -translate-x-1/2 -translate-y-1/2 transform'
          }
        }
      }
    )({
      style: props.variant
    })
  )

  const wrapBase = computed(() =>
    cva('relative flex w-full  md:m-0 md:flex-row md:gap-4', {
      variants: {
        style: {
          default: 'flex-col',
          block: 'flex-row'
        }
      }
    })({
      style: props.variant
    })
  )

  const isSwapped = ref(false)

  const swapAriaLabel = computed(() => {
    return t('aria.label.swapWithState', { from: selectedTitle.value.origin, to: selectedTitle.value.destin })
  })

  const onSwap = () => {
    isSwapped.value = !isSwapped.value
    origDestData.swapOrigDest()

    if (origDestData.selectedDestin.value) {
      onDestinationSelected()
    }

    announce(
      t('aria.label.swapAnnounce', {
        from: selectedTitle.value.origin,
        to: selectedTitle.value.destin
      })
    )
  }
</script>
<template>
  <div :class="wrapBase">
    <OrigDestSelect
      v-model="origDestData.selectedOrigin.value"
      v-model:searchTerm="searchTerm"
      v-model:category="origDestData.selectedCategory.value.origin"
      v-model:subCategory="origDestData.selectedSubCategory.value.origin"
      v-model:isOpen="isOriginOpen"
      :data="filteredOrigDestData"
      :categories="originCategories"
      :sub-categories="originSubCategories"
      :title="selectedTitle.origin"
      :label="$t('origDest.origin.flyFrom')"
      :trigger-aria-label="originTriggerAriaLabel"
      :variant="variant"
      :open-over-trigger="openOverTrigger"
      :type="ORIGIN"
      icon="common/takeoff"
      border="rounded-left-no-border"
      @update:is-open="openedOrigDest = ORIGIN"
    />
    <button
      v-if="allowSwap"
      :class="swapBase"
      :disabled="!origDestData.canSwapOrigDest.value"
      :aria-label="swapAriaLabel"
      :aria-pressed="isSwapped"
      @click="onSwap"
      @keydown.enter.prevent="onSwap"
      @keydown.space.prevent="onSwap"
    >
      <Icon
        name="common/round-loop"
        class="-scale-x-100"
        :class="{ '!opacity-30': !origDestData.canSwapOrigDest.value }"
      />
    </button>
    <OrigDestSelect
      v-model="origDestData.selectedDestin.value"
      v-model:searchTerm="searchTerm"
      v-model:category="origDestData.selectedCategory.value.destin"
      v-model:subCategory="origDestData.selectedSubCategory.value.destin"
      v-model:disabled="isDestinDisabled"
      :data="filteredOrigDestData"
      :categories="destinationCategories"
      :sub-categories="destinationSubCategories"
      :title="selectedTitle.destin"
      :label="$t('origDest.destin.flyTo')"
      :trigger-aria-label="destinationTriggerAriaLabel"
      :variant="variant"
      :open-over-trigger="openOverTrigger"
      :selected-origin="origDestData.selectedOrigin.value"
      :type="DESTIN"
      :show-any-destin-option="showAnyDestinOption"
      border="rounded-right"
      icon="common/land"
      @update:is-open="openedOrigDest = DESTIN"
      @selected="onDestinationSelected"
      @click="openOrigin"
      @keydown.enter="openOrigin"
    />
  </div>
</template>
